import { observable, runInAction } from 'mobx';

/**
 * Kill switch for the Faster TTFE "audio then video" experience (PLAT-11222):
 * the editor, Content Studio and the clip surfaces opening at transcript-ready
 * on an audio-first composition, then handing off to the video renditions
 * when they land.
 *
 * Every TTFE code path hangs off two predicates, and both consult this flag:
 * - `isCompressedRenditionPending` (audio-first mode in stores, services and
 *   components) reports "not pending" when the switch is off, so no surface
 *   treats a missing rendition as "not yet".
 * - `isStoryEditorEntryReady` falls back to the full `isStoryReady` gate, so a
 *   story is not enterable until fully processed — the pre-TTFE behaviour.
 *
 * The LaunchDarkly flag is `story-upload-ttfe-audio-then-video`
 * (`storyUploadTtfeAudioThenVideo` through the React SDK), synced into this
 * module by `useAudioThenVideoFlagSync` at the app root. It defaults OFF and
 * only an explicit `true` turns it on: a flag that is missing, not yet
 * evaluated, or unreachable leaves every surface on its pre-TTFE branch. A
 * kill switch that cannot be reached must fail towards the behaviour that was
 * shipping before, not towards the new one. The cost is a brief pre-TTFE
 * window at boot, until LaunchDarkly's first payload lands. Held in a MobX box
 * so observers re-render when the value lands.
 */
export const AUDIO_THEN_VIDEO_FLAG_KEY = 'story-upload-ttfe-audio-then-video';

/** The same flag as the React SDK spells it — `useFlags()` camel-cases keys. */
export const AUDIO_THEN_VIDEO_FLAG_CAMEL_KEY = 'storyUploadTtfeAudioThenVideo';

const enabled = observable.box(false);

export function isAudioThenVideoEnabled(): boolean {
  return enabled.get();
}

/** Only an explicit `true` counts as on (see module doc). */
export function setAudioThenVideoEnabled(value: unknown): void {
  runInAction(() => enabled.set(value === true));
}
